<script setup lang="tsx">
  import CustomDropdown from '~/components/UI/Forms/CustomDropdown.vue';
  import CustomInput from '~/components/UI/Forms/CustomInput.vue';
  import CustomTextArea from '~/components/UI/Forms/CustomTextArea.vue';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';
  import { get, post } from '~/services/donationScreen';
  import type { GetResponse, ResponseStatus, StockItemDrawer, PostResponse } from '~/types';
  import EditPasswordModal from '../../Modals/EditPasswordModal.vue';
  import AuditLogs from '../RoutedDrawerComponents/AuditLogs.vue';

  const props = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();

  const route = useRoute();
  const action = computed(() => {
    return route.query.action as string;
  });

  const formRef = ref();
  const formState = reactive({
    itemName: '',
    unitOfMeasurement: '',
    itemDescription: ''
  });
  const auditLogs = ref<any[]>([]);
  const status = ref<boolean>(false);
  const isModalVisible = ref<boolean>(false);
  const isModalVisibleForDisable = ref<boolean>(false);
  const editStatus = ref<ResponseStatus>('idle');

  const submitHandler = async (modalData: { password: string }) => {
    try {
      editStatus.value = 'pending';
      const response = await post<PostResponse<StockItemDrawer>>(`v1/stock_item/${props.identifier}`, {
        data: {
          name: formState.itemName,
          units: formState.unitOfMeasurement,
          description: formState.itemDescription,
          password: modalData.password
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Stock Item Updated Successfully');
        isModalVisible.value = false;
        props.onClose();
      } else if (response.data.status === 'error') {
        openFailedCustomNotification(response.data.message);
        isModalVisible.value = false;
        props.onClose();
      } else {
        openFailedCustomNotification('Failed to Update Stock Item');
        editStatus.value = 'error';
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Update Stock Item');
      editStatus.value = 'error';
    }
  };

  const handleCreate = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<PostResponse<StockItemDrawer>>('v1/add_new_stock_item', {
        data: {
          name: formState.itemName,
          units: formState.unitOfMeasurement,
          description: formState.itemDescription
        }
      });

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Stock Item Created Successfully');
        props.onClose();
        editStatus.value = 'success';
      } else if (response.data.status === 'error') {
        editStatus.value = 'error';
        return openFailedCustomNotification(response.data.message || 'Failed to Create Stock Item');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Create Stock Item');
      editStatus.value = 'error';
    }
  };

  const openDisableModal = async () => {
    try {
      isModalVisibleForDisable.value = true;
    } catch (error) {
      console.error(error);
    }
  };

  const handleDisableStockItems = async (modalData: { password: string }) => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/stock_item_disabled/${props.identifier}`, {
        data: {
          name: formState.itemName,
          units: formState.unitOfMeasurement,
          description: formState.itemDescription,
          is_disabled: true,
          password: modalData.password
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Stock Item Disabled Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to Disable Stock Item');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Disable Stock Item');
      editStatus.value = 'error';
    }
  };

  const handleActiveStockItem = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/stock_item_enabled/${props.identifier}`, {
        data: {
          is_disabled: false
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Stock Item Activated Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to Activate Stock Item');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Activate Stock Item');
      editStatus.value = 'error';
    }
  };

  const handleCreateStockItem = async () => {
    try {
      await formRef.value.validate();

      if (route.query.action === 'edit') {
        return openModal();
      }

      await handleCreate();
    } catch (error) {
      console.error(error);
    }
  };

  const openModal = async () => {
    try {
      const isFormValid = await formRef.value.validate();
      if (!isFormValid) throw new Error('Form validation failed!');

      isModalVisible.value = true;
    } catch (error) {
      console.error(error);
    }
  };

  const handleModalCancel = () => {
    isModalVisible.value = false;
  };

  const handleModalDisableCancel = () => {
    isModalVisibleForDisable.value = false;
  };

  const fetchData = async () => {
    try {
      const stockItemResponse = await get<GetResponse<StockItemDrawer>>(`v1/get_stock_item/${props.identifier}`);
      status.value = stockItemResponse?.data?.data?.selected_stock_item?.is_disabled;
      if (stockItemResponse.data) {
        Object.assign(formState, {
          itemName: stockItemResponse?.data?.data?.selected_stock_item?.name || '',
          unitOfMeasurement: stockItemResponse?.data?.data?.selected_stock_item?.units || '',
          itemDescription: stockItemResponse?.data?.data?.selected_stock_item?.description || ''
        });
        auditLogs.value = stockItemResponse.data.data.audit_log_data;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const rules = {
    itemName: [
      {
        required: true,
        message: 'Item Name is required',
        trigger: 'submit'
      }
    ],
    unitOfMeasurement: [
      {
        required: true,
        message: 'Unit of Measurement is required',
        trigger: 'submit'
      }
    ],
    itemDescription: []
  };

  const updateStateField = (value: string, stateKey: keyof typeof formState) => {
    (formState as any)[stateKey] = value;
  };

  onMounted(() => {
    if (props.identifier) {
      fetchData();
    }
  });
</script>

<template>
  <a-drawer
    :open="route.query.drawerFrom === 'stockItems'"
    :title="
      action === 'edit'
        ? 'Update Stock Item'
        : action === 'add' || action === 'duplicate'
          ? 'Add New Stock Item'
          : 'Stock Item'
    "
    width="750"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{
      padding: '12px',
      borderBottom: '1px solid #E4E4E7'
    }"
    @close="props.onClose"
  >
    <template #extra>
      <div class="flex gap-3">
        <RoleBasedButton
          v-if="!props.identifier || action"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded sm-medium duration-150 border border-blue-600"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="handleCreateStockItem"
        >
          {{ action === 'edit' ? 'Update Stock Item' : 'Create Stock Item' }}
        </RoleBasedButton>
        <RoleBasedButton
          v-if="action === 'edit' && !status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-red-600 hover:bg-red-700 text-white rounded sm-medium duration-150 border border-red-600 hover:!text-white hover:!border-red-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="openDisableModal"
        >
          {{ 'Disable Stock Item' }}
        </RoleBasedButton>
        <RoleBasedButton
          v-if="action === 'edit' && status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded sm-medium duration-150 border border-green-600 hover:!text-white hover:!border-green-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="handleActiveStockItem"
        >
          {{ 'Activate Stock Item' }}
        </RoleBasedButton>
      </div>
    </template>
    <a-form
      ref="formRef"
      class="flex flex-col gap-4 p-3 setup-drawer-form"
      :model="formState"
      :rules="rules"
      :colon="false"
      :label-col="{ style: { width: '250px', textAlign: 'left' } }"
      hide-required-mark="true"
    >
      <CustomInput
        label-text="Stock Item Name"
        name="itemName"
        :value="formState.itemName"
        state-key="itemName"
        :is-compulsory="true"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <CustomDropdown
        :options="[
          { title: 'Pieces', value: 'Pieces' },
          { title: 'Kg', value: 'Kg' },
          { title: 'Liters', value: 'Liters' },
          { title: 'Boxes', value: 'Boxes' },
          { title: 'Packets', value: 'Packets' },
          { title: 'Meters', value: 'Meters' },
          { title: 'Numbers', value: 'Numbers' },
          { title: 'Hours', value: 'Hours' }
        ]"
        label-text="Unit of Measurement"
        :label-width="'250px'"
        name="unitOfMeasurement"
        :value="formState.unitOfMeasurement"
        state-key="unitOfMeasurement"
        :rule="rules.unitOfMeasurement"
        :is-compulsory="true"
        :disabled="!action"
        @update:value="updateStateField"
      />
      <CustomTextArea
        label="Description"
        name="itemDescription"
        :value="formState.itemDescription"
        state-key="itemDescription"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
    </a-form>
    <AuditLogs :items="auditLogs" title="Stock Item" class="mx-3" />
    <EditPasswordModal
      :visible="isModalVisible"
      :loading="editStatus === 'pending'"
      modal-title="Update Stock Item"
      @update:cancel="handleModalCancel"
      @update:update="submitHandler"
    />
    <EditPasswordModal
      :visible="isModalVisibleForDisable"
      :loading="editStatus === 'pending'"
      modal-title="Disable Stock Item"
      :warning-message="'Are you sure you want to Disable this Stock Item?'"
      @update:cancel="handleModalDisableCancel"
      @update:update="handleDisableStockItems"
    />
  </a-drawer>
</template>

<style>
  @import '~/assets/css/donationDrawer.css';

  .setup-drawer-form .ant-form-item {
    margin-bottom: 0px;
  }
</style>
